import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import ButtonBase from '@material-ui/core/ButtonBase';
import Input from '@material-ui/core/Input';
import { makeStyles } from '@material-ui/core/styles';
import useTranslate from '@sprinx/react-globalize/useTranslate';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import Field from '@sprinx/react-mui-fields/Field';
import useJustifyQuantity from '../useJustifyQuantity';
var useStyles = makeStyles(function (theme) {
  return {
    root: {
      paddingTop: function paddingTop(props) {
        return props.label ? theme.spacing(2.5) : null;
      },
      width: theme.spacing(21)
    },
    wrapper: {
      width: '100%',
      fontSize: 3,
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'center',
      flexDirection: 'row'
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'center'
    },
    button: {
      flex: 0,
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(0.5)
    },
    input: {
      padding: theme.spacing(0.5),
      justifyItems: 'center',
      maxWidth: theme.spacing(9)
    },
    inputinput: {
      textAlign: 'center'
    },
    fullWidth: {
      width: '100%'
    },
    disabled: {},
    'size-small': {
      fontSize: theme.typography.pxToRem(13)
    },
    'size-medium': {
      fontSize: theme.typography.button.fontSize
    },
    'size-large': {
      fontSize: theme.typography.pxToRem(15)
    },
    rightAdornment: {},
    leftAdornment: {}
  };
}, {
  name: 'SxShoppingCartQuantityField'
});

var _ref2 = /*#__PURE__*/React.createElement(RemoveIcon, {
  color: "action",
  fontSize: "inherit"
});

var _ref3 = /*#__PURE__*/React.createElement(AddIcon, {
  color: "action",
  fontSize: "inherit"
});

function QuantityField(_ref) {
  var autoFocus = _ref.autoFocus,
      pClasses = _ref.classes,
      className = _ref.className,
      decButtonContent = _ref.decButtonContent,
      disabled = _ref.disabled,
      fullWidth = _ref.fullWidth,
      helperText = _ref.helperText,
      incButtonContent = _ref.incButtonContent,
      label = _ref.label,
      leftAdornment = _ref.leftAdornment,
      onChange = _ref.onChange,
      quantity = _ref.quantity,
      quantityMax = _ref.quantityMax,
      quantityMin = _ref.quantityMin,
      quantityStep = _ref.quantityStep,
      quantityStepRounding = _ref.quantityStepRounding,
      rightAdornment = _ref.rightAdornment,
      size = _ref.size,
      rest = _objectWithoutProperties(_ref, ["autoFocus", "classes", "className", "decButtonContent", "disabled", "fullWidth", "helperText", "incButtonContent", "label", "leftAdornment", "onChange", "quantity", "quantityMax", "quantityMin", "quantityStep", "quantityStepRounding", "rightAdornment", "size"]);

  var timer = React.useRef();
  var classes = useStyles({
    classes: pClasses,
    label: label
  });
  var t = useTranslate();

  var _React$useState = React.useState(undefined),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      statusText = _React$useState2[0],
      setStatusText = _React$useState2[1];

  var _React$useState3 = React.useState(quantity),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      inputValue = _React$useState4[0],
      setInputValue = _React$useState4[1];

  var justifyQuantity = useJustifyQuantity({
    quantityStep: quantityStep,
    quantityStepRounding: quantityStepRounding,
    quantityMin: quantityMin,
    quantityMax: quantityMax
  });
  var statusTextString = React.useMemo(function () {
    return t('shoppingCart/components/quantityField/states/quantityRounded');
  }, [t]);
  var displayStatusText = React.useCallback(function (pDisplay) {
    clearTimeout(timer.current);

    if (pDisplay) {
      setStatusText(statusTextString);
      timer.current = setTimeout(function () {
        setStatusText(undefined);
      }, 5000);
    } else {
      setStatusText(undefined);
    }
  }, [statusTextString]);
  React.useEffect(function () {
    var adjusted = justifyQuantity(quantity);
    setInputValue(adjusted); // displayStatusText(quantity !== adjusted);
  }, [displayStatusText, justifyQuantity, quantity]);
  var handleChange = React.useCallback(function (nextQuantity) {
    if (onChange && quantity !== nextQuantity) onChange(nextQuantity);
  }, [onChange, quantity]);

  var handleButtonClick = function handleButtonClick(dir) {
    return function () {
      handleChange(justifyQuantity(quantity + dir * quantityStep));
      displayStatusText(false);
    };
  };

  var handleInputBlur = React.useCallback(function (evnt) {
    var value = Number(evnt.target.value);

    if (!Number.isNaN(value)) {
      var adjusted = justifyQuantity(value);
      handleChange(adjusted);
      setInputValue(adjusted);
      displayStatusText(value !== adjusted);
    }
  }, [handleChange, justifyQuantity, displayStatusText]);
  return /*#__PURE__*/React.createElement(Field, _extends({}, rest, {
    label: label,
    helperText: statusText || helperText,
    className: clsx(classes.root, className, disabled && classes.disabled, fullWidth && classes.fullWidth),
    "data-testid": "quantity-component"
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.wrapper
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.leftAdornment
  }, leftAdornment), /*#__PURE__*/React.createElement("div", {
    className: classes.buttonContainer
  }, /*#__PURE__*/React.createElement(ButtonBase, {
    "data-testid": "quantity-field-minus",
    className: clsx(classes.button, classes["size-".concat(size)]),
    disabled: disabled,
    onClick: handleButtonClick(-1)
  }, decButtonContent || _ref2), /*#__PURE__*/React.createElement(Input, {
    "data-testid": "quantity-field-value",
    inputProps: {
      'data-testid': 'quantity-content-input'
    },
    className: classes.input,
    classes: {
      input: clsx(classes.inputinput, classes["size-".concat(size)])
    },
    value: inputValue,
    onChange: function onChange(e) {
      return setInputValue(e.target.value);
    },
    autoFocus: autoFocus,
    disabled: disabled,
    onBlur: handleInputBlur
  }), /*#__PURE__*/React.createElement(ButtonBase, {
    "data-testid": "quantity-field-plus",
    className: clsx(classes.button, classes["size-".concat(size)]),
    disabled: disabled,
    onClick: handleButtonClick(1),
    size: size
  }, incButtonContent || _ref3)), /*#__PURE__*/React.createElement("div", {
    className: classes.rightAdornment
  }, rightAdornment)));
}

process.env.NODE_ENV !== "production" ? QuantityField.propTypes = {
  autoFocus: PropTypes.bool,
  classes: PropTypes.shape({
    button: PropTypes.string,
    buttonContainer: PropTypes.string,
    disabled: PropTypes.string,
    fullWidth: PropTypes.string,
    input: PropTypes.string,
    inputinput: PropTypes.string,
    leftAdornment: PropTypes.string,
    rightAdornment: PropTypes.string,
    root: PropTypes.string,
    wrapper: PropTypes.string
  }),
  className: PropTypes.string,
  decButtonContent: PropTypes.node,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  helperText: PropTypes.string,
  incButtonContent: PropTypes.node,
  label: PropTypes.string,
  leftAdornment: PropTypes.node,
  onChange: PropTypes.func,
  quantity: PropTypes.number,
  quantityMax: PropTypes.number,
  quantityMin: PropTypes.number,
  quantityStep: PropTypes.number,
  quantityStepRounding: PropTypes.oneOf(['up', 'down']),
  rightAdornment: PropTypes.node,
  size: PropTypes.oneOf(['small', 'medium', 'large'])
} : void 0;
QuantityField.defaultProps = {
  autoFocus: false,
  classes: undefined,
  className: undefined,
  decButtonContent: undefined,
  disabled: false,
  fullWidth: false,
  helperText: undefined,
  incButtonContent: undefined,
  label: undefined,
  leftAdornment: undefined,
  onChange: undefined,
  quantity: 0,
  quantityMax: Number.MAX_SAFE_INTEGER,
  quantityMin: 1,
  quantityStep: 1,
  quantityStepRounding: undefined,
  rightAdornment: undefined,
  size: 'medium'
};
export default QuantityField;